import { useEffect, useRef, useState, type ReactNode } from 'react';
import { cn } from '@/lib/utils';

/**
 * Horizontal scroller for rail tab bars. On a rail too narrow for all its
 * tabs it:
 * - scrolls horizontally with the scrollbar hidden,
 * - fades the clipped edge(s) so the overflowing tabs are discoverable,
 * - supports mouse click-drag panning (a small movement threshold keeps
 *   plain tab clicks working; the click after a real drag is swallowed).
 *
 * Touch devices keep native panning (`overflow-x-auto`), so the drag logic
 * only engages for mouse pointers.
 */
export function RailTabScroller({
  children,
  className,
}: {
  children: ReactNode;
  /** Extra classes on the scroll container (e.g. `min-w-0 flex-1`). */
  className?: string;
}) {
  const scrollRef = useRef<HTMLDivElement>(null);
  const [clipped, setClipped] = useState({ left: false, right: false });
  const drag = useRef<{
    pointerId: number;
    startX: number;
    startScroll: number;
    dragged: boolean;
  } | null>(null);
  // Set when a pointerup ends a real drag; the very next click is swallowed
  // so releasing over a tab doesn't activate it.
  const suppressClick = useRef(false);

  // Track which edges clip content. Re-measured on scroll, container resize,
  // and every render (conditional tabs change scrollWidth without resizing
  // the container); the no-change guard keeps the state updates quiet.
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const update = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setClipped((prev) =>
        prev.left === left && prev.right === right ? prev : { left, right },
      );
    };
    update();
    el.addEventListener('scroll', update, { passive: true });
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => {
      el.removeEventListener('scroll', update);
      observer.disconnect();
    };
  });

  function onPointerDown(e: React.PointerEvent) {
    if (e.pointerType !== 'mouse' || e.button !== 0) return;
    const el = scrollRef.current;
    if (!el) return;
    drag.current = {
      pointerId: e.pointerId,
      startX: e.clientX,
      startScroll: el.scrollLeft,
      dragged: false,
    };
  }

  function onPointerMove(e: React.PointerEvent) {
    const d = drag.current;
    const el = scrollRef.current;
    if (!d || !el || e.pointerId !== d.pointerId) return;
    const dx = e.clientX - d.startX;
    if (!d.dragged && Math.abs(dx) > 4) {
      d.dragged = true;
      el.setPointerCapture(d.pointerId);
    }
    if (d.dragged) el.scrollLeft = d.startScroll - dx;
  }

  function endDrag(e: React.PointerEvent) {
    const d = drag.current;
    if (!d || e.pointerId !== d.pointerId) return;
    if (d.dragged) suppressClick.current = true;
    drag.current = null;
  }

  function onClickCapture(e: React.MouseEvent) {
    if (!suppressClick.current) return;
    suppressClick.current = false;
    e.preventDefault();
    e.stopPropagation();
  }

  // Fade the clipped edge(s) via a mask so the hint works on any background.
  const mask =
    clipped.left && clipped.right
      ? '[mask-image:linear-gradient(to_right,transparent,black_1.5rem,black_calc(100%-1.5rem),transparent)]'
      : clipped.left
        ? '[mask-image:linear-gradient(to_right,transparent,black_1.5rem)]'
        : clipped.right
          ? '[mask-image:linear-gradient(to_right,black_calc(100%-1.5rem),transparent)]'
          : undefined;

  return (
    <div
      ref={scrollRef}
      className={cn(
        'flex gap-1 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden',
        mask,
        className,
      )}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={endDrag}
      onPointerCancel={endDrag}
      onClickCapture={onClickCapture}
    >
      {children}
    </div>
  );
}
